import React, { useEffect, useRef, useState } from "react";
import { makeShellT } from "./i18n/index.js";

const CURRENT_VERSION = typeof __APP_VERSION__ === "string" ? __APP_VERSION__ : "0.0.0";

function compareSemver(a, b) {
  const aa = String(a || "").split(".").map((n) => Number(n) || 0);
  const bb = String(b || "").split(".").map((n) => Number(n) || 0);
  const len = Math.max(aa.length, bb.length);
  for (let i = 0; i < len; i += 1) {
    const left = aa[i] || 0;
    const right = bb[i] || 0;
    if (left > right) return 1;
    if (left < right) return -1;
  }
  return 0;
}

async function fetchLatestVersion() {
  const response = await fetch(`/version.json?ts=${Date.now()}`, { cache: "no-store" });
  if (!response.ok) throw new Error(`Version request failed: ${response.status}`);
  const payload = await response.json();
  if (!payload?.version) throw new Error("Missing version field");
  return payload.version;
}

/* One auto-reload attempt per detected version, so a CDN still serving the
   old build can't trap the app in a reload loop. sessionStorage scopes the
   guard to this tab and clears when the app is fully closed. */
const AUTO_UPDATE_GUARD = "solvente-auto-update-attempted";

/* Shared update state: checked once on mount. When the startup check finds a
   newer deployed version the app applies it seamlessly — refresh the service
   worker, then reload once so the network-first shell picks up the new build.
   The chip/banner remain for manual checks and as the guarded fallback. */
export function useUpdateCenter(notify) {
  const t = makeShellT();
  const [checking, setChecking] = useState(false);
  const [latestVersion, setLatestVersion] = useState(CURRENT_VERSION);
  const [hasUpdate, setHasUpdate] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const ranOnMount = useRef(false);

  async function check(manual = false) {
    if (typeof navigator !== "undefined" && !navigator.onLine) {
      if (manual) notify?.(t("app.update.offline"));
      return;
    }
    setChecking(true);
    try {
      const latest = await fetchLatestVersion();
      setLatestVersion(latest);
      const found = compareSemver(latest, CURRENT_VERSION) > 0;

      if ("serviceWorker" in navigator) {
        const registration = await navigator.serviceWorker.getRegistration();
        if (registration) await registration.update();
      }

      if (found && !manual) {
        let attempted = null;
        try { attempted = window.sessionStorage.getItem(AUTO_UPDATE_GUARD); } catch { /* private mode */ }
        if (attempted !== latest) {
          try { window.sessionStorage.setItem(AUTO_UPDATE_GUARD, latest); } catch { /* private mode */ }
          notify?.(t("app.update.installing"));
          setTimeout(() => window.location.reload(), 900);
          return;
        }
      }

      setHasUpdate(found);
      if (found) setDismissed(false);
      if (manual) notify?.(found ? `${t("app.update.available")} v${latest}` : t("app.update.upToDate"));
    } catch (err) {
      if (manual) notify?.(t("app.update.error"));
      console.error("[Aurio] update check failed", err);
    } finally {
      setChecking(false);
    }
  }

  useEffect(() => {
    if (ranOnMount.current) return;
    ranOnMount.current = true;
    check(false);
  }, []);

  return {
    version: CURRENT_VERSION,
    latestVersion,
    checking,
    hasUpdate,
    dismissed,
    dismiss: () => setDismissed(true),
    check,
    t,
  };
}

/* Compact version chip for the header row: `v0.5.0 ↻`. Tapping re-checks;
   when an update exists it shows a gold dot and tapping reloads. */
export function UpdateChip({ uc, T }) {
  const { t } = uc;
  const label = uc.hasUpdate ? t("app.update.reload") : t("app.update.check");
  return (
    <button
      onClick={uc.hasUpdate ? () => window.location.reload() : () => uc.check(true)}
      disabled={uc.checking}
      aria-label={label}
      title={label}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        background: T.panel,
        border: `1px solid ${T.line}`,
        borderRadius: 8,
        padding: "5px 8px",
        fontSize: 12,
        fontWeight: 600,
        letterSpacing: "0.03em",
        color: T.dim,
        cursor: uc.checking ? "wait" : "pointer",
      }}
    >
      v{uc.version}
      {uc.hasUpdate ? (
        <span aria-hidden="true" style={{ width: 7, height: 7, borderRadius: "50%", background: T.gold, display: "inline-block" }} />
      ) : (
        <span aria-hidden="true" style={{ color: T.gold, fontSize: 13, lineHeight: 1, opacity: uc.checking ? 0.4 : 1 }}>↻</span>
      )}
    </button>
  );
}

/* Slim dismissible banner shown under the header only when an update exists. */
export function UpdateBanner({ uc, T }) {
  const { t } = uc;
  if (!uc.hasUpdate || uc.dismissed) return null;
  return (
    <div
      role="status"
      style={{
        margin: "4px 20px 0",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 10,
        background: T.goldSoft,
        border: `1px solid ${T.gold}44`,
        borderRadius: 10,
        padding: "7px 8px 7px 12px",
        fontSize: 12.5,
        color: T.white,
      }}
    >
      <span>{t("app.update.ready").replace("{v}", uc.latestVersion)}</span>
      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
        <button
          onClick={() => window.location.reload()}
          style={{
            background: T.gold,
            color: T.midnight,
            border: "none",
            borderRadius: 99,
            padding: "5px 12px",
            fontSize: 12,
            fontWeight: 700,
            cursor: "pointer",
          }}
        >
          {t("app.update.refresh")}
        </button>
        <button
          onClick={uc.dismiss}
          aria-label={t("app.update.dismiss")}
          style={{ background: "none", border: "none", color: T.faint, fontSize: 16, cursor: "pointer", padding: "2px 6px" }}
        >
          ×
        </button>
      </div>
    </div>
  );
}
